import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ExtensionIcon from '@mui/icons-material/ExtensionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuthStore } from '../auth/authStore';
import { registeredModules } from '../app/modules';
import type { ModuleMenuItem } from '../app/moduleRegistry';

const DRAWER_WIDTH = 272;
const COLLAPSED_WIDTH = 72;
const COLLAPSE_STORAGE_KEY = 'tfw:sidebarCollapsed';

/** Sidebar section order — groups not listed here (shouldn't happen) render after these. */
const GROUP_ORDER = [
  'Uçak',
  'Otel',
  'Otobüs',
  'Transfer',
  'Araç Kiralama',
  'Turlar',
  'Sigorta',
  'Kurumsal',
  'Talepler',
  'Raporlar',
  'Pos',
  'Sistem',
  'Loglar',
  'B2C Yönetimi',
  'Yardım',
];

export function Sidebar() {
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.currentUser);

  const moduleMenuItems = registeredModules.flatMap((m) => m.menuItems);
  const visibleItems = moduleMenuItems.filter((item) => item.isVisible?.(currentUser) ?? true);

  const { groups, standaloneItems } = useMemo(() => {
    const byGroup = new Map<string, ModuleMenuItem[]>();
    const standalone: ModuleMenuItem[] = [];

    for (const item of visibleItems) {
      if (!item.group) {
        standalone.push(item);
        continue;
      }
      const list = byGroup.get(item.group) ?? [];
      list.push(item);
      byGroup.set(item.group, list);
    }

    const orderedGroupNames = [
      ...GROUP_ORDER.filter((name) => byGroup.has(name)),
      ...[...byGroup.keys()].filter((name) => !GROUP_ORDER.includes(name)),
    ];

    const orderedGroups = orderedGroupNames.map((name) => {
      const items = byGroup.get(name)!;
      return {
        name,
        icon: items.find((i) => i.groupIcon)?.groupIcon ?? <ExtensionIcon />,
        items,
      };
    });

    return { groups: orderedGroups, standaloneItems: standalone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems.length, currentUser]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1');

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const isGroupOpen = (name: string) => openGroups[name] ?? false;
  const toggleGroup = (name: string) =>
    setOpenGroups((prev) => ({ ...prev, [name]: !isGroupOpen(name) }));

  /** Daraltılmışken bir grup ikonuna tıklamak, o grubu açık hâlde menüyü tekrar
   *  genişletir — daraltılmış hâlde alt menüleri göstermeye yer olmadığından. */
  const handleGroupClick = (name: string) => {
    if (collapsed) {
      setCollapsed(false);
      if (!isGroupOpen(name)) toggleGroup(name);
      return;
    }
    toggleGroup(name);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shortest }),
        [`& .MuiDrawer-paper`]: {
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          overflowX: 'hidden',
          transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shortest }),
        },
      }}
    >
      {/* Boş spacer — Topbar `position: fixed` olduğundan bu şeridin üstünü kaplıyor;
          bu yüzden görünür içerik (toggle dahil) bunun ALTINDA render edilmeli, içinde değil. */}
      <Toolbar />
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: collapsed ? 0.5 : 1.5, pt: 1 }}>
        <IconButton onClick={() => setCollapsed((v) => !v)} size="small" aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}>
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
      <List component="nav" sx={{ px: collapsed ? 0.5 : 1.5, py: 2 }}>
        <Tooltip title={collapsed ? 'Anasayfa' : ''} placement="right">
          <ListItemButton
            component={RouterLink}
            to="/"
            selected={location.pathname === '/'}
            sx={{ borderRadius: 2, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
              <DashboardIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Anasayfa" />}
          </ListItemButton>
        </Tooltip>

        {groups.map((group) => {
          const open = !collapsed && isGroupOpen(group.name);
          return (
            <div key={group.name}>
              <Tooltip title={collapsed ? group.name : ''} placement="right">
                <ListItemButton
                  onClick={() => handleGroupClick(group.name)}
                  sx={{ borderRadius: 2, mt: 1, justifyContent: collapsed ? 'center' : 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                    {group.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {group.name}
                          </Typography>
                        }
                      />
                      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items.map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={RouterLink}
                      to={`/${item.path}`}
                      selected={location.pathname === `/${item.path}`}
                      sx={{ borderRadius: 2, pl: 4, ml: 1, mb: 0.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon ?? <ExtensionIcon fontSize="small" />}</ListItemIcon>
                      <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </div>
          );
        })}

        {standaloneItems.length > 0 && <Divider sx={{ my: 1.5 }} />}
        {standaloneItems.map((item) => (
          <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
            <ListItemButton
              component={RouterLink}
              to={`/${item.path}`}
              selected={location.pathname === `/${item.path}`}
              sx={{ borderRadius: 2, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                {item.icon ?? <ExtensionIcon />}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Drawer>
  );
}
